// SeatChip (UI-C8) and SpeakingIndicator (UI-C7). A seat is a 24 px dot in its seat colour carrying the
// seat's initial (identity never rests on hue alone); the speaking seat's chip glows from speech_start,
// with a ring that follows the level, and expands in place to show the name. Chips never reorder.
import type { CSSProperties } from "react";
import type { SeatView } from "../room/model";

/** The first letter of a name, as the reader sees it (grapheme-safe, upper case where the script has it). */
export function initialOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "·";
  const seg =
    typeof Intl.Segmenter === "function"
      ? new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(trimmed)[Symbol.iterator]().next().value
          ?.segment
      : undefined;
  return (seg ?? [...trimmed][0]).toLocaleUpperCase();
}

export function SeatDot({ seat, small }: { seat: SeatView; small?: boolean }) {
  return (
    <span className={`seat-dot seat-c${seat.colour}${small ? " small" : ""}`} aria-hidden="true">
      {initialOf(seat.name)}
    </span>
  );
}

export function SeatChip({
  seat,
  speaking,
  level,
}: {
  seat: SeatView;
  speaking: boolean;
  /** 0..1 when a level is known (this device's own mic); remote seats have none in M1. */
  level?: number;
}) {
  // A custom property set through the CSSOM (React's style prop), which the CSP allows.
  const style = speaking && level !== undefined ? ({ "--lvl": level.toFixed(2) } as CSSProperties) : undefined;
  return (
    <span
      className={`seat-chip seat-c${seat.colour}${speaking ? " speaking" : ""}${level !== undefined ? " has-level" : ""}`}
      data-seat={seat.id}
      data-spec="UI-C8 UI-C7 P-4 P-6 D-13"
      style={style}
    >
      <SeatDot seat={seat} />
      {speaking && (
        <span className="seat-name" dir="auto">
          {seat.name}
        </span>
      )}
    </span>
  );
}
